// vendors
import Vue from 'vue';
import VueModal from 'vue-js-modal';
import '@v4/common/utils/lazy-load';
import HeaderOfferLpThreeSteps from '@v4/header-offer-lp';

// misc
import { i18n, loadDefaultLanguage } from '@v4/locale/async';

// mixins
import { dkModalAware } from '@v4/common/components/mixins/modal';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import { modalClickToCall } from '@v4/common/components/mixins/modal-service';
import clickToCallModalHandler from '@v4/common/components/mixins/click-to-call-modal-handler';

// utils
import { EventBus } from '@v4/common/utils/event-bus';
import { getJsonDataFromScript } from '@v4/common/utils/json';
import { getQueryParams } from '@v4/common/utils/url';
import { isMobile } from '@v4/common/utils/media-query';
import { dealerSelectionStore } from '@v4/lead-form/store/index';

// api
import { fetchConfigurator } from '@v4/services/api/v3/car';
import { CLICK_TO_CALL_EVENTS } from '@v4/services/api/click-to-call/click-to-call';

(async () => {
    const el = document.getElementById('offer-lp');

    if (!el) {
        return;
    }

    Vue.use(VueModal, { dynamic: true, injectModalsContainer: true });

    await loadDefaultLanguage();

    new Vue({
        el,
        i18n,
        name: 'OfferLP',
        components: {
            LoadingWrapper: () => ({
                component: import(
                    /* webpackChunkName: "LoadingWrapper-vue" */ '@v4/common/components/atoms/LoadingWrapper'
                ),
            }),
            DkAccordion: () => ({
                component: import(
                    /* webpackChunkName: "DkAccordion-vue", webpackPreload: true */ '@v4/common/components/molecules/DkAccordionDST.vue'
                ),
            }),
            DkColorPicker: () => ({
                component: import(
                    /* webpackChunkName: "DkColorPicker-vue" */ '@v4/common/components/molecules/DkColorPicker.vue'
                ),
            }),
            LeadFormWrapper: () => ({
                component: import(
                    /* webpackChunkName: "LeadFormWrapper-vue" */ '@v4/common/components/organisms/LeadFormWrapper/LeadFormWrapper.vue'
                ),
            }),
            ClickoutBox: () => ({
                component: import(
                    /* webpackChunkName: "ClickoutBox-vue" */ '@/js/common/components/molecules/ClickoutBox.vue'
                ),
            }),
            DkELabel: () => ({
                component: import(/* webpackChunkName: "DkELabel-vue" */ '@v4/common/components/atoms/DkELabel.vue'),
            }),
            DkClickToCallSnackbar: () => ({
                component: import(
                    /* webpackChunkName: "ClickToCallSnackbar-vue" */ '@v4/common/components/organisms/click-to-call/DkClickToCallSnackbar.vue'
                ),
            }),
            DkBadge: () => ({
                component: import(/* webpackChunkName: "DkBadge-vue" */ '@v4/common/components/atoms/DkBadgeDST.vue'),
            }),
            DkButton: () => ({
                component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton'),
            }),
            DsVersionCard: () => ({
                component: import(
                    /* webpackChunkName: "DsVersionCard-vue" */ '@v4/common/components/molecules/DsVersionCard'
                ),
            }),
            DkIcon: () => ({
                component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
            }),
            DkDivider: () => ({
                component: import(/* webpackChunkName: "DkDivider-vue" */ '@v4/common/components/atoms/DkDivider.vue'),
            }),
            DkSlide: () => ({
                component: import(
                    /* webpackChunkName: "DkSlide-vue" */ '@v4/common/components/atoms/DkSlide/DkSlide.vue'
                ),
            }),
            DkSlider: () => ({
                component: import(
                    /* webpackChunkName: "DkSlider-vue" */ '@v4/common/components/molecules/DkSliderDST.vue'
                ),
            }),
            DkDealerInput: () => ({
                component: import(
                    /* webpackChunkName: "DkDealerInput-vue" */ '@v4/common/components/molecules/DkDealerInput.vue'
                ),
            }),
        },
        mixins: [dkModalAware, gaEventHandler, modalClickToCall, clickToCallModalHandler],
        data() {
            return {
                gaEvents: window.gaEvents || {},
                fetchedData: getJsonDataFromScript('#data-offer-lp') || {},
                colorsLoading: false,
                colors: [],
                hasColorSelector: null,
                selectedImageSrc: null,
                selectedImageIndex: 0,
                leadFormComponent: null,
                dataForLeadForm: null,
                formId: '',
                isMobile: isMobile(),
                currentStep: 1,
                hasDealerSelection: false,
                swiperOptions: {
                    spaceBetween: 24,
                    slidesPerView: 1,
                    breakpointsInverse: true,
                    watchOverflow: true,
                },
                galleryForDkPromoThreeSteps: [],
                smokeColor: '',
                fakeColors: ['black', 'white', 'red', 'gray', 'blue', 'others'],
                currentColorName: '',
                prefilledZip: null,
                isLeadFormLoading: true,
            };
        },
        computed: {
            perMonthString() {
                return this.fetchedData.vatText ? this.fetchedData.vatText.split(' ')[0] : '';
            },
            vatIncludedString() {
                return this.fetchedData.vatText
                    ? this.fetchedData.vatText
                        .split(' ')
                        .slice(1)
                        .join(' ')
                    : '';
            },
            hasClickout() {
                return !!getQueryParams().clickout;
            },
            hasTwoSteps() {
                return !this.hasClickout && !this.hasDealerSelection;
            },
            maxSteps() {
                return this.hasTwoSteps ? 2 : 3;
            },
            leadFormProperties() {
                return {
                    formId: this.formId,
                    formData: this.dataForLeadForm,
                    title: 'Inserisci i tuoi dati',
                };
            },
            galleryWithColorSelection() {
                return [
                    {
                        src: this.hasColorSelector ? this.selectedImageSrc : this.fetchedData.imageThreeQuarters,
                    },
                    ...this.galleryForDkPromoThreeSteps.filter((item) => !!item.src),
                ];
            },
            zipInputAttributes() {
                return {
                    required: true,
                    autocomplete: 'postal-code',
                    type: 'number',
                    minlength: 5,
                    maxlength: 5,
                    pattern: '^[0-9]{5}$',
                    placeholder: this.parsePlaceholderForMandatoryFields(this.$t('lead-form.input-zip-code-label')),
                    title: this.$t('lead-form.input-zip-code-error'),
                };
            },
            projectId() {
                return dealerSelectionStore.state.projectId;
            },
            makeName() {
                return this.fetchedData.makeUrlCode;
            },
            modelName() {
                return this.fetchedData.formData.submodel.submodelCommercialUrlCode;
            },
            isDealerSelected() {
                return dealerSelectionStore.state.selectedDealer;
            },
            colorsToShowOnLoad() {
                return this.isMobile ? 5 : this.hasDealerSelection ? 6 : 9;
            },
        },
        async mounted() {
            this.pushEvent('view_item');
            this.pushEvent('view_promotion');

            EventBus.$on('LeadForm:input', () => {
                this.pushEvent('begin_checkout');
            });

            EventBus.$on('DkPromo3Step:prefill-zip-code', (zipCode) => {
                this.prefilledZip = zipCode;
            });

            if (!this.colors.length) {
                this.colorsLoading = true;

                const { formData = {}, makeUrlCode = '' } = this.fetchedData;
                const { submodel = {} } = formData;

                // Do not fetch colors if it's a LCV site.
                // Information is not available for the LCV sites.
                if (!window.isLCVSite) {
                    try {
                        const result = await fetchConfigurator(
                            makeUrlCode,
                            submodel.submodelCommercialUrlCode,
                            !this.hasClickout ? { limitResults: 1 } : {}
                        );
                        const { data = {} } = result;
                        const { colors = {} } = data;
                        this.colors = colors.external || [];
                    } catch (e) {
                        this.colors = [];
                    }
                }

                if (this.colors.length > 0) {
                    this.onColorSelection(0);
                    this.hasColorSelector = true;
                } else {
                    this.hasColorSelector = false;
                }

                this.colorsLoading = false;
            }

            if (!this.clickout) {
                const { formId, formData, gallery } = this.fetchedData;
                const data = {
                    isThreeStepsDkPromo: true,
                    isDkPromo: formId === 'dkpromo',
                    ...formData,
                };
                this.showLeadFormInPage(formId, data);
                this.galleryForDkPromoThreeSteps = gallery;
            }

            if (this.isMobile) {
                this.setDefaultHash();
                window.addEventListener('popstate', (e) => this.handleHashNavigation(e));
            }

            EventBus.$on('DkLeadForm:has-dealer-selection', this.handleDealerSelection);
        },
        beforeDestroy() {
            EventBus.$off();
            if (this.isMobile) {
                window.removeEventListener('popstate', (e) => this.handleHashNavigation(e));
            }
        },
        methods: {
            showLeadFormModal(formId = '', formData = {}) {
                return import(
                    /* webpackChunkName: "SimpleLeadFormModal-vue" */ '@v4/common/components/organisms/SimpleLeadFormModal'
                ).then(
                    async ({ default: SimpleLeadFormModal }) =>
                        await this.showModal(
                            SimpleLeadFormModal,
                            {
                                formId,
                                formData,
                                title: this.$t('vdp.lead-form.title'),
                                subtitle: this.$t('vdp.lead-form.subtitle'),
                            },
                            {
                                name: 'simple-lead-form-modal',
                                height: 'auto',
                                classes: 'modal--fullscreen_medium',
                                width: 680,
                                scrollable: true,
                            },
                            {},
                            {
                                disableBodyScroll: false,
                            }
                        )
                );
            },
            onCtaClick(formId = '', formData = {}) {
                this.showLeadFormModal(formId, formData);
            },
            async onColorSelection(index) {
                const color = this.colors[index];
                this.currentColorName = color.description;

                if (!color) {
                    return;
                }

                this.colors = this.colors.map((item) => ({ ...item, active: false }));
                this.$set(this.colors, index, { ...color, active: true });

                const src = (color.colorImage && color.colorImage.image800) || '';

                if (!src) {
                    return;
                }

                let img;
                const loadImagePromise = new Promise((resolve) => {
                    img = new Image();
                    img.onload = resolve;
                    img.src = src;
                });

                await loadImagePromise;

                if (this.selectedImageIndex !== 0) {
                    this.selectedImageIndex = 0;
                }

                this.selectedImageSrc = img.src;

                if (this.clickout) {
                    return;
                }

                if (this.isMobile) {
                    this.$refs.mobileSlider.swiper.slideTo(0);
                }
            },
            onLeadFormMounted() {
                let color = this.colors.find((color) => color.selected) || {};

                color = Object.entries(color).reduce(
                    (acc, [key, value]) => ({ ...acc, [key]: ['default', value].filter((item) => item).join(' - ') }),
                    {}
                );

                this.setLeadFormColorHiddenFields(color);
            },
            setLeadFormColorHiddenFields(color = {}) {
                if (
                    !this.$refs.leadFormWrapper ||
                    typeof this.$refs.leadFormWrapper.setLeadFormHiddenField !== 'function'
                ) {
                    return;
                }

                this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[colorDescription]', color.description || '');
                this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[colorCode]', color.baseColourHex || '');
                this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[colorType]', color.group || '');
                this.$refs.leadFormWrapper.setLeadFormHiddenField('dkwp[colorCalculated]', color.primaryHex || '');
                this.$refs.leadFormWrapper.setLeadFormHiddenField(
                    'dkwp[secondaryColorCalculated]',
                    color.secondaryHex || ''
                );
            },
            onCtaChange(payload) {
                if (payload.cta && payload.cta.fields && payload.cta.fields.length) {
                    payload.cta.fields.forEach(({ name, value }) => {
                        if (name && value) {
                            this.$refs.leadFormWrapper.setLeadFormHiddenField(name, value);
                        }
                    });
                }
            },
            pushEvent(name) {
                if (!name || !this.gaEvents[name]) {
                    return;
                }

                this.pushGtagEvent(name, null, this.gaEvents);
            },
            onClickToCall() {
                this.trackClickToCallClick(CLICK_TO_CALL_EVENTS.trigger);
                this.handleClickToCallModal();
            },
            onImageSelection(index) {
                this.selectedImageIndex = index;
            },
            parsePlaceholderForMandatoryFields(placeholderText) {
                return `${placeholderText}*`;
            },
            loadLeadForm() {
                return import(
                    /* webpackChunkName: "LeadFormModal-vue" */ '@v4/common/components/organisms/LeadFormWrapper/LeadFormWrapper'
                );
            },
            showLeadFormInPage(formId = '', formData = {}) {
                if (!formData || !formId) {
                    return;
                }

                this.formId = formId;
                this.dataForLeadForm = formData;

                const leadForm = this.loadLeadForm();

                leadForm.then(() => {
                    this.leadFormComponent = () => leadForm;
                });
            },
            showNextStep() {
                this.currentStep = this.currentStep + 1;
                this.emitStepUpdate();
                this.handleHashChange();
            },
            onColorsOrderChange(index) {
                const [activeObject] = this.colors.splice(index, 1);
                this.colors.unshift(activeObject);
                this.onColorSelection(0);
            },
            setDefaultHash() {
                const hashes = window.location.hash;

                if (hashes[0]) {
                    window.history.replaceState(null, null, window.location.pathname + window.location.search);
                }
            },
            handleHashChange() {
                const stepMapping = {
                    dealers: this.currentStep === 2 && this.hasDealerSelection,
                    price: this.currentStep === 3 || (this.currentStep === 2 && !this.hasDealerSelection),
                };

                Object.entries(stepMapping).forEach(([hash, condition]) => {
                    if (condition) {
                        window.location.hash = hash;
                    }
                });
            },
            handleHashNavigation(e) {
                const currentHash = e.target.location.hash || '';

                if (!currentHash) {
                    this.currentStep = 1;
                } else if (this.hasDealerSelection) {
                    this.handleHashNavigationWithDealerSelection(currentHash);
                } else {
                    this.handleHashWithoutDealerSelection(currentHash);
                }

                this.emitStepUpdate();
            },
            handleHashNavigationWithDealerSelection(currentHash) {
                if (currentHash === '#dealers') {
                    this.currentStep = 2;
                } else if (currentHash === '#price') {
                    this.currentStep = this.isDealerSelected ? 3 : 2;
                    if (!this.isDealerSelected) {
                        window.location.hash = 'dealers';
                    }
                }

                this.emitStepUpdate();
            },
            handleHashWithoutDealerSelection(currentHash) {
                if (currentHash === '#price') {
                    this.currentStep = 2;
                }
            },
            emitStepUpdate() {
                EventBus.$emit('DkPromoThreeSteps:next-step', this.currentStep);

                if (this.currentStep === this.maxSteps) {
                    EventBus.$emit('DkPromoThreeSteps:is-last-step');
                }
            },
            handleDealerSelection(hasDealerSelection) {
                this.hasDealerSelection = hasDealerSelection;
                this.isLeadFormLoading = false;

                if (!hasDealerSelection) {
                    EventBus.$emit('DkPromoThreeSteps:set-two-steps');
                }
            },
        },
    });
    if (document.getElementById('header-dkpromo-three-steps')) {
        HeaderOfferLpThreeSteps();
    }
})();
